import React, { useState, useEffect, useCallback } from 'react';
import {
  MagnifyingGlass,
  Plus,
  PencilSimple,
  ShoppingCart,
  ClockCounterClockwise,
  Export,
  FileCsv,
  FileXls,
  FilePdf,
  ArrowsClockwise,
  Path,
  WhatsappLogo,
  Package,
  Invoice,
  Star,
  UserPlus,
  ArrowUp,
  ArrowDown,
  ArrowLeft,
  CaretRight,
  MapPin,
  Phone,
  EnvelopeSimple,
} from '@phosphor-icons/react';
import { Header } from '../../components/layout';
import { Modal, Button, ToastContainer, useToast } from '../../components/common';
import { customerService, societyService, slotService, serviceService, orderService } from '../../services';
import './Customers.css';


export function Customers({ onMenuClick }) {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailTab, setDetailTab] = useState('overview'); // overview | orders | timeline
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerOrders, setCustomerOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [journeyData, setJourneyData] = useState(null);
  const [timelineNewestFirst, setTimelineNewestFirst] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  // Order form state
  const [services, setServices] = useState([]);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [orderFormData, setOrderFormData] = useState({
    orderSource: 'Call',
    isPickupScheduled: true,
    pickupDate: '',
    pickupSlot: '',
    deliveryDate: '',
    serviceCategory: '',
    serviceType: 'Standard',
    deliveryType: 'Home Delivery'
  });
  const [orderSubmitting, setOrderSubmitting] = useState(false);
  const [selectedGarmentCategory, setSelectedGarmentCategory] = useState('Men');
  const [itemSearchQuery, setItemSearchQuery] = useState('');
  const [itemQuantities, setItemQuantities] = useState({});

  // Toast notifications
  const toast = useToast();

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCustomers, setTotalCustomers] = useState(0);
  const ITEMS_PER_PAGE = 20;

  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    pincode: '',
    society: '',
    societyId: '',
    address: '',
  });

  const [availableSocieties, setAvailableSocieties] = useState([]);

  // Transform backend customer to UI format
  const transformCustomer = (customer) => {
    const currentAddress = customer.addresses?.find(a => a.isCurrent) || customer.addresses?.[0];
    return {
      id: customer._id,
      name: customer.customerName,
      phone: customer.phone,
      email: customer.email || '',
      source: customer.source || 'other',
      lastWhatsAppAt: customer.lastWhatsAppAt || null,
      society: currentAddress?.society?.name || 'N/A',
      societyId: currentAddress?.society?._id || currentAddress?.society,
      address: currentAddress?.address || '',
      pincode: currentAddress?.pincode || '',
      addresses: customer.addresses || [],
      orderCount: customer.orderCount || 0,
      totalSpent: customer.totalSpent || 0,
      createdAt: customer.createdAt ? new Date(customer.createdAt).toISOString().split('T')[0] : '',
      createdAtRaw: customer.createdAt || null,
    };
  };

  const sourceLabel = (source) => {
    const map = {
      whatsapp: 'WhatsApp',
      'walk-in': 'Walk-in',
      mobile: 'App',
      bot: 'Bot',
      staff: 'Staff',
      other: 'Manual',
    };
    return map[source] || 'Manual';
  };

  const waDigits = (phone) => {
    const d = String(phone || '').replace(/\D/g, '');
    if (!d) return '';
    if (d.length === 10) return `91${d}`;
    return d;
  };

  const statusClass = (status) =>
    `cust-status cust-status--${String(status || 'unknown').toLowerCase().replace(/\s+/g, '-')}`;

  const formatShortDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const formatRelative = (dateStr) => {
    if (!dateStr) return null;
    const t = new Date(dateStr).getTime();
    if (Number.isNaN(t)) return null;
    const hours = (Date.now() - t) / (1000 * 60 * 60);
    if (hours < 1) return 'Just now';
    if (hours < 24) return `${Math.floor(hours)}h ago`;
    if (hours < 48) return 'Yesterday';
    if (hours < 24 * 7) return `${Math.floor(hours / 24)}d ago`;
    return formatShortDate(dateStr);
  };

  const isWithinCareWindow = (lastAt) => {
    if (!lastAt) return false;
    return Date.now() - new Date(lastAt).getTime() < 24 * 60 * 60 * 1000;
  };

  // Fetch customers from API
  const fetchCustomers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        page: currentPage,
        limit: ITEMS_PER_PAGE,
        search: searchQuery || undefined,
        sortKey: sortConfig.key || 'createdAt',
        sortOrder: sortConfig.direction,
      };

      const response = await customerService.getCustomers(params);

      if (response && response.customers) {
        const transformedCustomers = response.customers.map(transformCustomer);
        setCustomers(transformedCustomers);
        setTotalCustomers(response.total || 0);
        setTotalPages(Math.ceil((response.total || 0) / ITEMS_PER_PAGE) || 1);
      } else {
        setCustomers([]);
        setTotalCustomers(0);
        setTotalPages(1);
      }
    } catch (err) {
      console.error('Error fetching customers:', err);
      setError('Failed to load customers. Please try again.');
      setCustomers([]);
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchQuery, sortConfig]);

  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  const handlePincodeChange = async (pincode) => {
    setFormData({ ...formData, pincode, society: '', societyId: '' });
    if (pincode.length === 6) {
      try {
        const societies = await societyService.getSocietiesByPincode(pincode);
        setAvailableSocieties(societies.map(s => ({
          id: s._id,
          name: s.name,
          pincode: s.pincode
        })));
      } catch (err) {
        console.error('Error fetching societies:', err);
        setAvailableSocieties([]);
      }
    } else {
      setAvailableSocieties([]);
    }
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const sortedCustomers = [...customers].sort((a, b) => {
    if (!sortConfig.key) return 0;
    const aVal = a[sortConfig.key];
    const bVal = b[sortConfig.key];
    if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
    return 0;
  });

  const filteredCustomers = sortedCustomers.filter(
    (customer) =>
      customer.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      customer.phone.includes(searchQuery) ||
      customer.society.toLowerCase().includes(searchQuery.toLowerCase()) ||
      customer.pincode.includes(searchQuery)
  );

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  const handleRefresh = () => {
    setCurrentPage(1);
    fetchCustomers();
  };

  const handleExport = (format) => {
    const dataToExport = filteredCustomers;

    if (format === 'csv') {
      const headers = ['ID', 'Name', 'Phone', 'Society', 'Address', 'Pincode', 'Orders', 'Total Spent', 'Created'];
      const csvRows = [
        headers.join(','),
        ...dataToExport.map(customer => [
          customer.id,
          customer.name,
          customer.phone,
          customer.society,
          customer.address,
          customer.pincode,
          customer.orderCount,
          customer.totalSpent,
          customer.createdAt
        ].join(','))
      ];
      downloadFile(csvRows.join('\n'), 'customers-export.csv', 'text/csv');
    } else if (format === 'json') {
      downloadFile(JSON.stringify(dataToExport, null, 2), 'customers-export.json', 'application/json');
    } else if (format === 'pdf') {
      window.print();
    }
    setShowExportMenu(false);
  };

  const downloadFile = (content, filename, type) => {
    const blob = new Blob([content], { type });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleAddCustomer = () => {
    setIsEditing(false);
    setFormData({ name: '', phone: '', pincode: '', society: '', societyId: '', address: '' });
    setAvailableSocieties([]);
    setShowAddModal(true);
  };

  const handleEditCustomer = async (customer) => {
    setIsEditing(true);
    setSelectedCustomer(customer);
    setFormData({
      name: customer.name,
      phone: customer.phone,
      pincode: customer.pincode,
      society: customer.society,
      societyId: customer.societyId || '',
      address: customer.address,
    });

    // Fetch societies for the pincode
    if (customer.pincode?.length === 6) {
      try {
        const societies = await societyService.getSocietiesByPincode(customer.pincode);
        setAvailableSocieties(societies.map(s => ({
          id: s._id,
          name: s.name,
          pincode: s.pincode
        })));
      } catch (err) {
        console.error('Error fetching societies:', err);
        setAvailableSocieties([]);
      }
    } else {
      setAvailableSocieties([]);
    }

    setShowAddModal(true);
  };

  const handleCreateOrder = async (customer) => {
    setSelectedCustomer(customer);
    setOrderFormData({
      orderSource: 'Call',
      isPickupScheduled: true,
      pickupDate: '',
      pickupSlot: '',
      deliveryDate: '',
      serviceCategory: '',
      serviceType: 'Standard',
      deliveryType: 'Home Delivery'
    });
    setItemQuantities({});
    setSelectedGarmentCategory('Men');
    setItemSearchQuery('');
    setShowOrderModal(true);

    // Fetch services if not already loaded
    if (services.length === 0) {
      try {
        const servicesData = await serviceService.getServices();
        setServices(servicesData || []);
        // Auto-select first service
        if (servicesData && servicesData.length > 0) {
          setOrderFormData(prev => ({ ...prev, serviceCategory: servicesData[0]._id }));
        }
      } catch (err) {
        console.error('Error fetching services:', err);
        toast.error('Failed to load services');
      }
    } else if (services.length > 0) {
      setOrderFormData(prev => ({ ...prev, serviceCategory: services[0]._id }));
    }
  };

  // Calculate delivery date based on service type
  const calculateDeliveryDate = (pickupDate, serviceType) => {
    if (!pickupDate) return '';
    const pickup = new Date(pickupDate);
    let daysToAdd = 3; // Standard: 4th day (3 days after pickup)

    if (serviceType === 'Express') {
      daysToAdd = 1; // Next day
    } else if (serviceType === 'Semi Express') {
      daysToAdd = 2; // Day after tomorrow
    }

    pickup.setDate(pickup.getDate() + daysToAdd);
    return pickup.toISOString().split('T')[0];
  };

  const handlePickupDateChange = async (date) => {
    const deliveryDate = calculateDeliveryDate(date, orderFormData.serviceType);
    setOrderFormData({ ...orderFormData, pickupDate: date, pickupSlot: '', deliveryDate });
    if (date) {
      try {
        const slots = await slotService.getAvailablePickupSlots(date);
        setAvailableSlots(slots || []);
      } catch (err) {
        console.error('Error fetching slots:', err);
        setAvailableSlots([]);
      }
    } else {
      setAvailableSlots([]);
    }
  };

  // Update delivery date when service type changes
  const handleServiceTypeChange = (serviceType) => {
    const deliveryDate = calculateDeliveryDate(orderFormData.pickupDate, serviceType);
    setOrderFormData({ ...orderFormData, serviceType, deliveryDate });
  };

  const getSelectedService = () => services.find(s => s._id === orderFormData.serviceCategory);

  const getPriceMultiplier = () => {
    const service = getSelectedService();
    if (orderFormData.serviceType === 'Semi Express') return service?.semiExpressPriceMultiplier || 1.5;
    if (orderFormData.serviceType === 'Express') return service?.expressPriceMultiplier || 2;
    return 1;
  };

  const getServiceGarmentCategories = () => {
    const service = getSelectedService();
    const fallback = ['Men', 'Women', 'Kids', 'Shoes', 'Boots', 'Sandals', 'Slippers', 'Others', 'Household'];
    if (!service || !service.subcategories || service.subcategories.length === 0) return fallback;
    const cats = [...new Set(service.subcategories.map(s => s.garmentCategory).filter(Boolean))];
    return cats.length > 0 ? cats : fallback;
  };

  const getFilteredSubcategories = () => {
    const service = getSelectedService();
    if (!service?.subcategories) return [];
    return service.subcategories.filter(item => {
      const matchesCategory = item.garmentCategory === selectedGarmentCategory;
      const matchesSearch = !itemSearchQuery ||
        item.itemName.toLowerCase().includes(itemSearchQuery.toLowerCase()) ||
        (item.garmentCode && item.garmentCode.toLowerCase().includes(itemSearchQuery.toLowerCase()));
      return matchesCategory && matchesSearch;
    });
  };

  const handleItemQuantityChange = (itemName, qty) => {
    setItemQuantities(prev => ({ ...prev, [itemName]: Math.max(0, parseFloat(qty) || 0) }));
  };

  const handleOrderSubmit = async () => {
    if (orderFormData.isPickupScheduled && (!orderFormData.pickupDate || !orderFormData.pickupSlot)) {
      toast.error('Please select pickup date and slot');
      return;
    }

    const service = getSelectedService();
    if (!service) {
      toast.error('Please select a service');
      return;
    }

    const multiplier = getPriceMultiplier();
    let items = [];

    if (service.pricingModel === 'PerKg') {
      const weight = parseFloat(itemQuantities['weight']) || 0;
      if (weight <= 0) { toast.error('Please enter the weight'); return; }
      const pricePerKg = service.pricePerKg * multiplier;
      items = [{ serviceCategoryID: service._id, serviceCategoryName: service.categoryName, serviceType: orderFormData.serviceType, pricingModel: 'PerKg', itemName: service.categoryName, weightInKg: weight, pricePerKg, quantity: weight, itemTotal: weight * pricePerKg }];
    } else if (service.pricingModel === 'PerItem') {
      items = Object.entries(itemQuantities)
        .filter(([, qty]) => qty > 0)
        .map(([itemName, qty]) => {
          const sub = service.subcategories.find(s => s.itemName === itemName);
          if (!sub) return null;
          return { serviceCategoryID: service._id, serviceCategoryName: service.categoryName, serviceType: orderFormData.serviceType, pricingModel: 'PerItem', itemName: sub.itemName, garmentCode: sub.garmentCode || '', garmentCategory: sub.garmentCategory, quantity: qty, pricePerItem: sub.price * multiplier, itemTotal: qty * sub.price * multiplier };
        })
        .filter(Boolean);
      if (items.length === 0) { toast.error('Please add at least one item'); return; }
    } else if (service.pricingModel === 'PerPair') {
      const pairs = parseFloat(itemQuantities['pairs']) || 0;
      if (pairs <= 0) { toast.error('Please enter the number of pairs'); return; }
      const pricePerPair = service.pricePerPair * multiplier;
      items = [{ serviceCategoryID: service._id, serviceCategoryName: service.categoryName, serviceType: orderFormData.serviceType, pricingModel: 'PerPair', itemName: service.categoryName, pairCount: pairs, pricePerPair, quantity: pairs, itemTotal: pairs * pricePerPair }];
    }

    const subtotal = items.reduce((sum, item) => sum + item.itemTotal, 0);
    setOrderSubmitting(true);
    try {
      const orderData = {
        customerID: selectedCustomer.id,
        orderSource: orderFormData.orderSource,
        isPickupScheduled: orderFormData.isPickupScheduled,
        serviceType: orderFormData.serviceType,
        pickupDate: orderFormData.pickupDate || undefined,
        pickupSlot: orderFormData.pickupSlot || undefined,
        deliveryDate: orderFormData.deliveryDate || undefined,
        expectedDeliveryDate: orderFormData.deliveryDate || undefined,
        deliveryType: orderFormData.deliveryType,
        discountPercentage: 0,
        subtotal,
        finalAmount: subtotal,
        items
      };
      await orderService.createOrder(orderData);
      toast.success('Order created successfully');
      setShowOrderModal(false);
      fetchCustomers();
      if (detailOpen && selectedCustomer) {
        openCustomerDetail(selectedCustomer, detailTab === 'timeline' ? 'timeline' : 'orders');
      }
    } catch (err) {
      console.error('Error creating order:', err);
      toast.error(err.response?.data?.message || 'Failed to create order');
    } finally {
      setOrderSubmitting(false);
    }
  };

  const openCustomerDetail = async (customer, tab = 'overview') => {
    setSelectedCustomer(customer);
    setCustomerOrders([]);
    setJourneyData(null);
    setDetailTab(tab);
    setDetailOpen(true);
    setOrdersLoading(true);

    try {
      const journey = await customerService.getCustomerJourney(customer.id);
      setJourneyData(journey);
      setCustomerOrders(journey?.orders || []);
      // Enrich list row fields from journey when available
      if (journey?.customer) {
        setSelectedCustomer((prev) => ({
          ...prev,
          name: journey.customer.customerName || prev.name,
          phone: journey.customer.phone || prev.phone,
          email: journey.customer.email || prev.email,
          source: journey.customer.source || prev.source,
          lastWhatsAppAt: journey.customer.lastWhatsAppAt || prev.lastWhatsAppAt,
          addresses: journey.customer.addresses || prev.addresses,
        }));
      }
    } catch (err) {
      console.error('Error fetching customer journey:', err);
      try {
        const response = await customerService.getCustomerOrders(customer.id);
        const orders = Array.isArray(response) ? response : (response?.orders || []);
        setCustomerOrders(orders);
      } catch {
        setCustomerOrders([]);
      }
    } finally {
      setOrdersLoading(false);
    }
  };

  const closeCustomerDetail = () => {
    setDetailOpen(false);
    setJourneyData(null);
    setCustomerOrders([]);
    setDetailTab('overview');
  };

  const formatJourneyDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const getJourneyEventIcon = (type) => {
    switch (type) {
      case 'customer_created': return UserPlus;
      case 'order_created': return Package;
      case 'order_status': return Path;
      case 'whatsapp': return WhatsappLogo;
      case 'invoice_available':
      case 'invoice_sent': return Invoice;
      case 'feedback_received': return Star;
      default: return ClockCounterClockwise;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.societyId) {
      toast.error('Please select a society for the pincode');
      return;
    }

    try {
      const customerData = {
        customerName: formData.name,
        phone: formData.phone,
        address: formData.address,
        society: formData.societyId,
        pincode: formData.pincode,
      };

      if (isEditing && selectedCustomer) {
        await customerService.updateCustomer(selectedCustomer.id, customerData);
        toast.success('Customer updated successfully');
        if (detailOpen) {
          openCustomerDetail({
            ...selectedCustomer,
            name: formData.name,
            phone: formData.phone,
            address: formData.address,
            pincode: formData.pincode,
            society: formData.society,
            societyId: formData.societyId,
          });
        }
      } else {
        await customerService.createCustomer(customerData);
        toast.success('Customer added successfully');
      }

      setShowAddModal(false);
      fetchCustomers();
    } catch (err) {
      console.error('Error saving customer:', err);
      toast.error(err.response?.data?.message || 'Failed to save customer');
    }
  };

  return (
    <>
      <Header
        title={detailOpen && selectedCustomer ? selectedCustomer.name : 'Customers'}
        subtitle={
          detailOpen && selectedCustomer
            ? `${selectedCustomer.phone}${selectedCustomer.society && selectedCustomer.society !== 'N/A' ? ` · ${selectedCustomer.society}` : ''}`
            : 'Find a customer, open their profile, book and track in one place'
        }
        onMenuClick={onMenuClick}
        actions={
          detailOpen && selectedCustomer ? (
            <div className="header-actions-group">
              <Button
                variant="secondary"
                icon={<ArrowLeft size={18} />}
                onClick={closeCustomerDetail}
              >
                Back to list
              </Button>
              <Button
                variant="secondary"
                icon={<PencilSimple size={18} />}
                onClick={() => handleEditCustomer(selectedCustomer)}
              >
                Edit
              </Button>
              <Button
                icon={<ShoppingCart size={18} />}
                onClick={() => handleCreateOrder(selectedCustomer)}
              >
                New order
              </Button>
            </div>
          ) : (
            <div className="header-actions-group">
              <button className="header-icon-btn" onClick={handleRefresh} title="Refresh" type="button">
                <ArrowsClockwise size={18} />
              </button>
              <div className="export-wrapper">
                <button
                  className="header-icon-btn"
                  type="button"
                  onClick={() => setShowExportMenu(!showExportMenu)}
                  title="Export"
                >
                  <Export size={18} />
                </button>
                {showExportMenu && (
                  <div className="export-dropdown">
                    <button type="button" onClick={() => handleExport('csv')}>
                      <FileCsv size={18} /> Export CSV
                    </button>
                    <button type="button" onClick={() => handleExport('json')}>
                      <FileXls size={18} /> Export JSON
                    </button>
                    <button type="button" onClick={() => handleExport('pdf')}>
                      <FilePdf size={18} /> Print / PDF
                    </button>
                  </div>
                )}
              </div>
              <Button icon={<Plus size={18} />} onClick={handleAddCustomer}>
                Add customer
              </Button>
            </div>
          )
        }
      />
      <div className={`page-content customers-page${detailOpen ? ' customers-page--detail' : ''}`}>
        {!detailOpen ? (
          <>
            <div className="search-bar-wrapper">
              <div className="search-wrapper">
                <MagnifyingGlass className="search-icon" size={20} />
                <input
                  type="search"
                  className="search-input"
                  placeholder="Search name, phone, society, pincode…"
                  value={searchQuery}
                  onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
                  aria-label="Search customers"
                />
              </div>
              {!loading && !error && (
                <p className="customers-count" aria-live="polite">
                  {totalCustomers} customer{totalCustomers === 1 ? '' : 's'}
                </p>
              )}
            </div>

            {loading ? (
              <div className="cust-skeleton-list" aria-busy="true" aria-label="Loading customers">
                {Array.from({ length: 8 }).map((_, i) => (
                  <div key={i} className="cust-skeleton-row" />
                ))}
              </div>
            ) : error ? (
              <div className="error-state">
                <div className="error-icon">!</div>
                <p className="error-message">{error}</p>
                <button type="button" className="retry-btn" onClick={handleRefresh}>Try again</button>
              </div>
            ) : (
              <div className="table-container">
                <table className="table customers-table scrollable">
                  <thead>
                    <tr>
                      <th>Customer</th>
                      <th>Contact</th>
                      <th>Location</th>
                      <th
                        className="sortable"
                        onClick={() => handleSort('orderCount')}
                      >
                        Orders
                        {sortConfig.key === 'orderCount' && (
                          <span className="sort-indicator">
                            {sortConfig.direction === 'asc' ? ' ↑' : ' ↓'}
                          </span>
                        )}
                      </th>
                      <th
                        className="sortable"
                        onClick={() => handleSort('totalSpent')}
                      >
                        Lifetime
                        {sortConfig.key === 'totalSpent' && (
                          <span className="sort-indicator">
                            {sortConfig.direction === 'asc' ? ' ↑' : ' ↓'}
                          </span>
                        )}
                      </th>
                      <th className="text-center">Quick</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredCustomers.length === 0 ? (
                      <tr>
                        <td colSpan="6" className="empty-state">
                          <p className="empty-state-title">No customers match</p>
                          <p className="empty-state-hint">
                            Try another search, or add a customer to start booking.
                          </p>
                          <Button icon={<Plus size={16} />} onClick={handleAddCustomer}>
                            Add customer
                          </Button>
                        </td>
                      </tr>
                    ) : (
                      filteredCustomers.map((customer) => (
                        <tr
                          key={customer.id}
                          className="customer-row"
                          onClick={() => openCustomerDetail(customer)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              openCustomerDetail(customer);
                            }
                          }}
                          tabIndex={0}
                          role="button"
                          aria-label={`Open profile for ${customer.name}`}
                        >
                          <td>
                            <div className="customer-info">
                              <div className="customer-avatar" aria-hidden>
                                {(customer.name || '?').charAt(0).toUpperCase()}
                              </div>
                              <div className="customer-identity">
                                <span className="customer-name customer-name--link">
                                  {customer.name || 'Unnamed'}
                                </span>
                                <span className={`source-pill source-pill--${customer.source || 'other'}`}>
                                  {sourceLabel(customer.source)}
                                </span>
                              </div>
                            </div>
                          </td>
                          <td>
                            <span className="customer-phone">{customer.phone}</span>
                            {customer.lastWhatsAppAt && isWithinCareWindow(customer.lastWhatsAppAt) && (
                              <span className="wa-window-dot" title="WhatsApp free-form window open">
                                WA open
                              </span>
                            )}
                          </td>
                          <td>
                            <span className="society-name">{customer.society}</span>
                            {(customer.address || customer.pincode) && (
                              <span className="address-line">
                                {[customer.address, customer.pincode].filter(Boolean).join(' · ')}
                              </span>
                            )}
                          </td>
                          <td className="text-center">
                            <span className="order-count">{customer.orderCount}</span>
                          </td>
                          <td className="text-right font-semibold">
                            {formatCurrency(customer.totalSpent)}
                          </td>
                          <td onClick={(e) => e.stopPropagation()}>
                            <div className="actions-cell">
                              <button
                                type="button"
                                className="action-btn primary"
                                title="New order"
                                onClick={() => handleCreateOrder(customer)}
                              >
                                <ShoppingCart size={16} />
                              </button>
                              <button
                                type="button"
                                className="action-btn"
                                title="Open profile"
                                onClick={() => openCustomerDetail(customer)}
                              >
                                <CaretRight size={16} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}

            {!loading && !error && totalPages > 1 && (
              <div className="pagination-wrapper">
                <span className="pagination-info">
                  Showing {((currentPage - 1) * ITEMS_PER_PAGE) + 1}–{Math.min(currentPage * ITEMS_PER_PAGE, totalCustomers)} of {totalCustomers}
                </span>
                <div className="pagination-controls">
                  <button type="button" className="pagination-btn" onClick={() => setCurrentPage(1)} disabled={currentPage === 1}>«</button>
                  <button type="button" className="pagination-btn" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}>‹ Prev</button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1)
                    .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 2)
                    .reduce((acc, p, idx, arr) => {
                      if (idx > 0 && p - arr[idx - 1] > 1) acc.push('...');
                      acc.push(p);
                      return acc;
                    }, [])
                    .map((item, idx) =>
                      item === '...' ? (
                        <span key={`ellipsis-${idx}`} className="pagination-ellipsis">…</span>
                      ) : (
                        <button
                          key={item}
                          type="button"
                          className={`pagination-btn${currentPage === item ? ' active' : ''}`}
                          onClick={() => setCurrentPage(item)}
                        >
                          {item}
                        </button>
                      )
                    )}
                  <button type="button" className="pagination-btn" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}>Next ›</button>
                  <button type="button" className="pagination-btn" onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages}>»</button>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="customer-profile">
            {ordersLoading && !journeyData ? (
              <div className="cust-profile-loading" aria-busy="true">
                <div className="spinner" />
                <span>Loading profile…</span>
              </div>
            ) : (
              <>
                <section className="cust-hero" aria-label="Customer summary">
                  <div className="cust-hero-main">
                    <div className="customer-avatar customer-avatar--lg" aria-hidden>
                      {(selectedCustomer?.name || '?').charAt(0).toUpperCase()}
                    </div>
                    <div className="cust-hero-copy">
                      <h2 className="cust-hero-name">{selectedCustomer?.name || 'Customer'}</h2>
                      <div className="cust-hero-meta">
                        <a
                          className="cust-meta-link"
                          href={`tel:${selectedCustomer?.phone}`}
                        >
                          <Phone size={16} weight="bold" />
                          {selectedCustomer?.phone}
                        </a>
                        {selectedCustomer?.phone && (
                          <a
                            className="cust-meta-link cust-meta-link--wa"
                            href={`https://wa.me/${waDigits(selectedCustomer.phone)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <WhatsappLogo size={16} weight="fill" />
                            WhatsApp
                          </a>
                        )}
                        {selectedCustomer?.email && (
                          <a className="cust-meta-link" href={`mailto:${selectedCustomer.email}`}>
                            <EnvelopeSimple size={16} />
                            {selectedCustomer.email}
                          </a>
                        )}
                      </div>
                      <div className="cust-hero-tags">
                        <span className={`source-pill source-pill--${selectedCustomer?.source || 'other'}`}>
                          {sourceLabel(selectedCustomer?.source)}
                        </span>
                        {selectedCustomer?.lastWhatsAppAt && (
                          <span className={`wa-window-chip${isWithinCareWindow(selectedCustomer.lastWhatsAppAt) ? ' wa-window-chip--open' : ''}`}>
                            {isWithinCareWindow(selectedCustomer.lastWhatsAppAt)
                              ? `Care window open · ${formatRelative(selectedCustomer.lastWhatsAppAt)}`
                              : `Last WhatsApp ${formatRelative(selectedCustomer.lastWhatsAppAt)}`}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <dl className="cust-stat-strip">
                    <div>
                      <dt>Orders</dt>
                      <dd>{journeyData?.stats?.totalOrders ?? selectedCustomer?.orderCount ?? 0}</dd>
                    </div>
                    <div>
                      <dt>Open</dt>
                      <dd>{journeyData?.stats?.openOrders ?? '—'}</dd>
                    </div>
                    <div>
                      <dt>Lifetime</dt>
                      <dd>{formatCurrency(journeyData?.stats?.lifetimeSpend ?? selectedCustomer?.totalSpent ?? 0)}</dd>
                    </div>
                    <div>
                      <dt>Avg order</dt>
                      <dd>
                        {journeyData?.stats?.avgOrderValue != null
                          ? formatCurrency(journeyData.stats.avgOrderValue)
                          : '—'}
                      </dd>
                    </div>
                    <div>
                      <dt>Customer since</dt>
                      <dd>
                        {formatShortDate(
                          journeyData?.customer?.createdAt || selectedCustomer?.createdAtRaw || selectedCustomer?.createdAt
                        )}
                      </dd>
                    </div>
                  </dl>
                </section>

                <div className="cust-tabs" role="tablist" aria-label="Customer sections">
                  {[
                    { id: 'overview', label: 'Overview' },
                    { id: 'orders', label: `Orders${customerOrders.length ? ` (${customerOrders.length})` : ''}` },
                    { id: 'timeline', label: 'Activity' },
                  ].map((tab) => (
                    <button
                      key={tab.id}
                      type="button"
                      role="tab"
                      aria-selected={detailTab === tab.id}
                      className={`cust-tab${detailTab === tab.id ? ' is-active' : ''}`}
                      onClick={() => setDetailTab(tab.id)}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>

                <div className="cust-tab-panel" role="tabpanel">
                  {detailTab === 'overview' && (
                    <div className="cust-overview">
                      <section className="cust-section">
                        <h3 className="cust-section-title">Addresses</h3>
                        {(selectedCustomer?.addresses || []).length === 0 && !selectedCustomer?.address ? (
                          <p className="journey-empty-hint">
                            No saved address yet. Edit the customer or place an order with a delivery address.
                          </p>
                        ) : (
                          <ul className="cust-address-list">
                            {(selectedCustomer?.addresses?.length
                              ? selectedCustomer.addresses
                              : [{
                                  address: selectedCustomer.address,
                                  pincode: selectedCustomer.pincode,
                                  society: { name: selectedCustomer.society },
                                  isCurrent: true,
                                }]
                            ).map((addr, idx) => (
                              <li key={addr._id || idx} className={`cust-address${addr.isCurrent ? ' is-current' : ''}`}>
                                <MapPin size={18} className="cust-address-icon" />
                                <div>
                                  <p className="cust-address-line">
                                    {addr.address || '—'}
                                    {addr.isCurrent && <span className="addr-current">Current</span>}
                                  </p>
                                  <p className="cust-address-sub">
                                    {[addr.society?.name || addr.society, addr.pincode].filter(Boolean).join(' · ')}
                                  </p>
                                </div>
                              </li>
                            ))}
                          </ul>
                        )}
                      </section>

                      <section className="cust-section">
                        <h3 className="cust-section-title">Channels & activity</h3>
                        <div className="cust-kv">
                          <div>
                            <span className="cust-kv-label">Order sources</span>
                            <span className="cust-kv-value">
                              {(journeyData?.stats?.channels || []).length
                                ? journeyData.stats.channels.join(', ')
                                : 'None yet'}
                            </span>
                          </div>
                          <div>
                            <span className="cust-kv-label">First order</span>
                            <span className="cust-kv-value">
                              {formatShortDate(journeyData?.stats?.firstOrderAt)}
                            </span>
                          </div>
                          <div>
                            <span className="cust-kv-label">Last order</span>
                            <span className="cust-kv-value">
                              {formatShortDate(journeyData?.stats?.lastOrderAt)}
                            </span>
                          </div>
                          <div>
                            <span className="cust-kv-label">Repeat orders</span>
                            <span className="cust-kv-value">
                              {journeyData?.stats?.repeatOrders ?? 0}
                            </span>
                          </div>
                        </div>
                      </section>

                      <section className="cust-section cust-recent">
                        <div className="cust-section-head">
                          <h3 className="cust-section-title">Recent orders</h3>
                          {customerOrders.length > 0 && (
                            <button type="button" className="text-btn" onClick={() => setDetailTab('orders')}>
                              View all
                            </button>
                          )}
                        </div>
                        {customerOrders.length === 0 ? (
                          <p className="journey-empty-hint">
                            No orders yet.{' '}
                            <button type="button" className="text-btn inline" onClick={() => handleCreateOrder(selectedCustomer)}>
                              Create the first order
                            </button>
                          </p>
                        ) : (
                          <ul className="cust-order-preview">
                            {customerOrders.slice(0, 4).map((order) => (
                              <li key={order.orderId || order._id}>
                                <div>
                                  <span className="mono">{order.orderId}</span>
                                  <span className="muted"> · {order.serviceName || '—'}</span>
                                </div>
                                <div className="cust-order-preview-right">
                                  <span className={statusClass(order.orderStatus)}>{order.orderStatus}</span>
                                  <span className="amount">{formatCurrency(order.billAmount || 0)}</span>
                                </div>
                              </li>
                            ))}
                          </ul>
                        )}
                      </section>
                    </div>
                  )}

                  {detailTab === 'orders' && (
                    <div className="cust-orders-panel">
                      {customerOrders.length === 0 ? (
                        <p className="journey-empty-hint">No orders for this customer yet.</p>
                      ) : (
                        <div className="table-container">
                          <table className="table journey-orders-table">
                            <thead>
                              <tr>
                                <th>Order</th>
                                <th>Service</th>
                                <th>Status</th>
                                <th>Placed</th>
                                <th>Pickup</th>
                                <th className="text-right">Amount</th>
                                <th />
                              </tr>
                            </thead>
                            <tbody>
                              {customerOrders.map((order) => (
                                <tr key={order.orderId || order._id}>
                                  <td className="mono">{order.orderId}</td>
                                  <td>{order.serviceName || '—'}</td>
                                  <td>
                                    <span className={statusClass(order.orderStatus)}>
                                      {order.orderStatus}
                                    </span>
                                  </td>
                                  <td>{formatShortDate(order.orderedOn)}</td>
                                  <td>{formatShortDate(order.pickupDate)}</td>
                                  <td className="text-right font-semibold">
                                    {formatCurrency(order.billAmount || 0)}
                                  </td>
                                  <td>
                                    {order.invoiceUrl && (
                                      <a
                                        className="journey-link"
                                        href={order.invoiceUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                      >
                                        Invoice
                                      </a>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                      <div className="history-total">
                        <span>Lifetime spend</span>
                        <span className="total-value">
                          {formatCurrency(
                            journeyData?.stats?.lifetimeSpend ?? selectedCustomer?.totalSpent ?? 0
                          )}
                        </span>
                      </div>
                    </div>
                  )}

                  {detailTab === 'timeline' && (
                    <div className="journey-timeline-section">
                      <div className="journey-timeline-header">
                        <h3 className="cust-section-title">Lifetime activity</h3>
                        <button
                          type="button"
                          className="journey-sort-btn"
                          onClick={() => setTimelineNewestFirst((v) => !v)}
                        >
                          {timelineNewestFirst ? (
                            <><ArrowDown size={14} /> Newest first</>
                          ) : (
                            <><ArrowUp size={14} /> Oldest first</>
                          )}
                        </button>
                      </div>
                      {!(journeyData?.timeline?.length) ? (
                        <p className="journey-empty-hint">
                          No activity yet. Orders and WhatsApp messages will show up here.
                        </p>
                      ) : (
                        <ol className="journey-timeline">
                          {[...(journeyData.timeline || [])]
                            .sort((a, b) => {
                              const da = new Date(a.at).getTime();
                              const db = new Date(b.at).getTime();
                              return timelineNewestFirst ? db - da : da - db;
                            })
                            .map((event) => {
                              const Icon = getJourneyEventIcon(event.type);
                              return (
                                <li
                                  key={event.id}
                                  className={`journey-event journey-event--${event.type}${event.approx ? ' journey-event--approx' : ''}${event.success === false ? ' journey-event--failed' : ''}`}
                                >
                                  <div className="journey-event-marker">
                                    <Icon size={14} weight="bold" />
                                  </div>
                                  <div className="journey-event-body">
                                    <div className="journey-event-top">
                                      <span className="journey-event-title">{event.title}</span>
                                      <time className="journey-event-time">{formatJourneyDate(event.at)}</time>
                                    </div>
                                    {event.detail && (
                                      <p className="journey-event-detail">{event.detail}</p>
                                    )}
                                    <div className="journey-event-meta">
                                      {event.orderId && (
                                        <span className="journey-chip mono">{event.orderId}</span>
                                      )}
                                      {event.channel && (
                                        <span className="journey-chip">{event.channel}</span>
                                      )}
                                      {event.templateName && (
                                        <span className="journey-chip">{event.templateName}</span>
                                      )}
                                      {event.status && (
                                        <span className="journey-chip">{event.status}</span>
                                      )}
                                      {event.approx && (
                                        <span className="journey-chip journey-chip--muted">approx</span>
                                      )}
                                      {event.success === false && (
                                        <span className="journey-chip journey-chip--fail">failed</span>
                                      )}
                                      {event.success === true && event.type === 'whatsapp' && (
                                        <span className="journey-chip journey-chip--ok">sent</span>
                                      )}
                                      {event.invoiceUrl && (
                                        <a
                                          className="journey-link"
                                          href={event.invoiceUrl}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                        >
                                          Invoice
                                        </a>
                                      )}
                                      {event.amount != null && event.amount > 0 && (
                                        <span className="journey-chip">{formatCurrency(event.amount)}</span>
                                      )}
                                    </div>
                                  </div>
                                </li>
                              );
                            })}
                        </ol>
                      )}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Add/Edit Customer Modal */}
      <Modal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        title={isEditing ? 'Edit Customer' : 'Add New Customer'}
        footer={
          <>
            <Button variant="secondary" onClick={() => setShowAddModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleSubmit}>
              {isEditing ? 'Save Changes' : 'Add Customer'}
            </Button>
          </>
        }
      >
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">Pincode</label>
            <input
              type="text"
              className="form-input"
              placeholder="Enter 6-digit pincode"
              maxLength={6}
              value={formData.pincode}
              onChange={(e) => handlePincodeChange(e.target.value.replace(/\D/g, ''))}
              required
            />
            {formData.pincode.length === 6 && availableSocieties.length === 0 && (
              <p className="form-error">No societies found for this pincode</p>
            )}
          </div>

          <div className="form-group">
            <label className="form-label">Phone Number</label>
            <input
              type="tel"
              className="form-input"
              placeholder="10-digit phone number"
              maxLength={10}
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value.replace(/\D/g, '') })}
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label">Full Name</label>
            <input
              type="text"
              className="form-input"
              placeholder="Enter customer name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label">Society</label>
            <div className="society-options">
              {availableSocieties.length > 0 ? (
                availableSocieties.map((society) => (
                  <label key={society.id} className="society-option">
                    <input
                      type="radio"
                      name="society"
                      value={society.name}
                      checked={formData.society === society.name}
                      onChange={() => setFormData({ ...formData, society: society.name, societyId: society.id })}
                    />
                    <span>{society.name}</span>
                  </label>
                ))
              ) : (
                <p className="form-hint">Enter a valid pincode to see societies</p>
              )}
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Address (Flat No, Block)</label>
            <input
              type="text"
              className="form-input"
              placeholder="E.g., A-123, Tower 4"
              value={formData.address}
              onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              required
            />
          </div>
        </form>
      </Modal>

      {/* Create Order Modal */}
      <Modal
        isOpen={showOrderModal}
        onClose={() => setShowOrderModal(false)}
        title={`Create New Order for ${selectedCustomer?.name || ''}`}
        size="xl"
        footer={
          <>
            <Button variant="secondary" onClick={() => setShowOrderModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleOrderSubmit} loading={orderSubmitting}>
              Create Order
            </Button>
          </>
        }
      >
        <div className="order-booking-form">
          {/* Top Row: Source, Pickup, Delivery */}
          <div className="booking-header-row">
            <div className="form-group compact">
              <label className="form-label">Source</label>
              <div className="radio-group-inline">
                {['Call', 'Walk-in', 'App', 'Store Visit'].map(src => (
                  <label key={src} className={`radio-pill ${orderFormData.orderSource === src ? 'active' : ''}`}>
                    <input type="radio" name="source" value={src} checked={orderFormData.orderSource === src} onChange={(e) => setOrderFormData({ ...orderFormData, orderSource: e.target.value })} />
                    {src}
                  </label>
                ))}
              </div>
            </div>
            <div className="form-group compact">
              <label className="form-label">Pickup</label>
              <div className="radio-group-inline">
                <label className={`radio-pill ${orderFormData.isPickupScheduled ? 'active' : ''}`}>
                  <input type="radio" name="pickup" checked={orderFormData.isPickupScheduled} onChange={() => setOrderFormData({ ...orderFormData, isPickupScheduled: true })} />
                  Schedule
                </label>
                <label className={`radio-pill ${!orderFormData.isPickupScheduled ? 'active' : ''}`}>
                  <input type="radio" name="pickup" checked={!orderFormData.isPickupScheduled} onChange={() => setOrderFormData({ ...orderFormData, isPickupScheduled: false, pickupDate: '', pickupSlot: '' })} />
                  Walk-in
                </label>
              </div>
            </div>
            <div className="form-group compact">
              <label className="form-label">Delivery</label>
              <div className="radio-group-inline">
                <label className={`radio-pill ${orderFormData.deliveryType === 'Home Delivery' ? 'active' : ''}`}>
                  <input type="radio" name="delivery" value="Home Delivery" checked={orderFormData.deliveryType === 'Home Delivery'} onChange={(e) => setOrderFormData({ ...orderFormData, deliveryType: e.target.value })} />
                  Home
                </label>
                <label className={`radio-pill ${orderFormData.deliveryType === 'Store Pick-up' ? 'active' : ''}`}>
                  <input type="radio" name="delivery" value="Store Pick-up" checked={orderFormData.deliveryType === 'Store Pick-up'} onChange={(e) => setOrderFormData({ ...orderFormData, deliveryType: e.target.value })} />
                  Store
                </label>
              </div>
            </div>
          </div>

          {/* Pickup Date/Slot Row */}
          {orderFormData.isPickupScheduled && (
            <div className="pickup-schedule-row">
              <div className="form-group compact">
                <label className="form-label">Pickup Date</label>
                <input type="date" className="form-input" value={orderFormData.pickupDate} min={new Date().toISOString().split('T')[0]} onChange={(e) => handlePickupDateChange(e.target.value)} />
              </div>
              <div className="form-group compact">
                <label className="form-label">Slot</label>
                <select className="form-select" value={orderFormData.pickupSlot} onChange={(e) => setOrderFormData({ ...orderFormData, pickupSlot: e.target.value })}>
                  <option value="">Select slot</option>
                  {availableSlots.map((slot) => {
                    const available = slot.maxCapacity - slot.bookedCount;
                    const isFull = available <= 0;
                    return (
                      <option key={slot._id} value={slot._id} disabled={isFull}>
                        {slot.slotName} — {isFull ? 'Not Available' : `${slot.bookedCount}/${slot.maxCapacity} booked (${available} left)`}
                      </option>
                    );
                  })}
                </select>
              </div>
            </div>
          )}

          {/* Service Selection + Service Type */}
          <div className="service-selection-row">
            <div className="form-group">
              <label className="form-label">Service</label>
              <select
                className="form-select"
                value={orderFormData.serviceCategory}
                onChange={(e) => {
                  const newId = e.target.value;
                  setOrderFormData({ ...orderFormData, serviceCategory: newId });
                  setItemQuantities({});
                  const svc = services.find(s => s._id === newId);
                  const firstCat = svc?.subcategories?.[0]?.garmentCategory;
                  setSelectedGarmentCategory(firstCat || 'Men');
                }}
              >
                <option value="">Select service</option>
                {services.map(s => (
                  <option key={s._id} value={s._id}>{s.categoryName}</option>
                ))}
              </select>
            </div>
            <div className="service-type-tabs">
              {['Standard', 'Semi Express', 'Express'].map((type) => (
                <button
                  key={type}
                  type="button"
                  className={`service-type-tab ${orderFormData.serviceType === type ? 'active' : ''}`}
                  onClick={() => { handleServiceTypeChange(type); setItemQuantities({}); }}
                >
                  {type}
                  {type === 'Semi Express' && <span className="multiplier">1.5x</span>}
                  {type === 'Express' && <span className="multiplier">2x</span>}
                </button>
              ))}
            </div>
          </div>

          {/* Delivery Date Info */}
          {orderFormData.pickupDate && orderFormData.deliveryDate && (
            <div className="delivery-date-info">
              <div className="date-info-item">
                <span className="date-label">Pickup</span>
                <span className="date-value">{new Date(orderFormData.pickupDate).toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' })}</span>
              </div>
              <div className="date-arrow">→</div>
              <div className="date-info-item">
                <span className="date-label">Est. Delivery</span>
                <span className="date-value highlight">{new Date(orderFormData.deliveryDate).toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' })}</span>
              </div>
              <span className="delivery-type-badge">
                {orderFormData.serviceType === 'Express' ? '1 Day' : orderFormData.serviceType === 'Semi Express' ? '2 Days' : '3 Days'}
              </span>
            </div>
          )}

          {/* PerKg input */}
          {getSelectedService()?.pricingModel === 'PerKg' && (
            <div className="perkg-section">
              <div className="form-group">
                <label className="form-label">Weight (kg)</label>
                <input
                  type="number"
                  className="form-input"
                  placeholder="Enter weight"
                  min="0"
                  step="0.1"
                  value={itemQuantities['weight'] || ''}
                  onChange={(e) => setItemQuantities(prev => ({ ...prev, weight: e.target.value }))}
                />
              </div>
              <div className="perkg-price">
                Price: ₹{((getSelectedService()?.pricePerKg || 0) * getPriceMultiplier()).toFixed(2)} per kg
                {parseFloat(itemQuantities['weight'] || 0) > 0 && (
                  <div style={{ marginTop: 4, fontWeight: 600, color: 'var(--primary)' }}>
                    Total: ₹{(parseFloat(itemQuantities['weight']) * (getSelectedService()?.pricePerKg || 0) * getPriceMultiplier()).toFixed(2)}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* PerItem input */}
          {getSelectedService()?.pricingModel === 'PerItem' && (
            <div className="items-selection-section">
              <div className="item-search-box">
                <MagnifyingGlass className="search-icon" size={16} />
                <input
                  type="text"
                  className="form-input"
                  placeholder="Search items..."
                  value={itemSearchQuery}
                  onChange={(e) => setItemSearchQuery(e.target.value)}
                />
              </div>
              <div className="garment-category-tabs">
                {getServiceGarmentCategories().map(cat => (
                  <button key={cat} type="button" className={`garment-tab ${selectedGarmentCategory === cat ? 'active' : ''}`} onClick={() => setSelectedGarmentCategory(cat)}>
                    {cat}
                  </button>
                ))}
              </div>
              <div className="items-grid">
                {getFilteredSubcategories().length === 0 ? (
                  <div className="no-items-message">No items found</div>
                ) : (
                  getFilteredSubcategories().map(item => {
                    const qty = itemQuantities[item.itemName] || 0;
                    const price = (item.price * getPriceMultiplier()).toFixed(2);
                    return (
                      <div key={item.itemName} className={`item-card ${qty > 0 ? 'selected' : ''}`}>
                        <div className="item-card-header">
                          <span className="item-name">{item.itemName}</span>
                          <span className="item-code">{item.garmentCode}</span>
                        </div>
                        <div className="item-card-footer">
                          <span className="item-price">₹{price}</span>
                          <div className="qty-controls">
                            <button type="button" className="qty-btn" disabled={qty === 0} onClick={() => handleItemQuantityChange(item.itemName, qty - 1)}>−</button>
                            <span className="qty-input">{qty}</span>
                            <button type="button" className="qty-btn" onClick={() => handleItemQuantityChange(item.itemName, qty + 1)}>+</button>
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
              {Object.values(itemQuantities).some(q => q > 0) && (
                <div style={{ textAlign: 'right', fontSize: '0.875rem', fontWeight: 600, color: 'var(--primary)' }}>
                  Total: ₹{(
                    Object.entries(itemQuantities)
                      .filter(([, q]) => q > 0)
                      .reduce((sum, [name, qty]) => {
                        const sub = getSelectedService()?.subcategories?.find(s => s.itemName === name);
                        return sum + (sub ? sub.price * getPriceMultiplier() * qty : 0);
                      }, 0)
                  ).toFixed(2)}
                </div>
              )}
            </div>
          )}

          {/* PerPair input */}
          {getSelectedService()?.pricingModel === 'PerPair' && (
            <div className="perkg-section">
              <div className="form-group">
                <label className="form-label">Number of Pairs</label>
                <input
                  type="number"
                  className="form-input"
                  placeholder="Enter pairs"
                  min="0"
                  step="1"
                  value={itemQuantities['pairs'] || ''}
                  onChange={(e) => setItemQuantities(prev => ({ ...prev, pairs: e.target.value }))}
                />
              </div>
              <div className="perkg-price">
                Price: ₹{((getSelectedService()?.pricePerPair || 0) * getPriceMultiplier()).toFixed(2)} per pair
                {parseFloat(itemQuantities['pairs'] || 0) > 0 && (
                  <div style={{ marginTop: 4, fontWeight: 600, color: 'var(--primary)' }}>
                    Total: ₹{(parseFloat(itemQuantities['pairs']) * (getSelectedService()?.pricePerPair || 0) * getPriceMultiplier()).toFixed(2)}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </Modal>

      {/* Toast Notifications */}
      <ToastContainer toasts={toast.toasts} removeToast={toast.removeToast} />
    </>
  );
}

export default Customers;
